import { type ReactNode, useState } from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { InfoIcon, PencilIcon, PlusIcon } from "@phosphor-icons/react";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { z } from "zod";

import {
  Button,
  classNames,
  Input,
  Text,
  Tooltip,
  toastError,
} from "@alpacahq/ui/old";

import { formatCash } from "../../common/util";

const zBalanceSchema = z.object({
  balance: z.coerce.number().optional(),
});

type BalanceSchema = z.infer<typeof zBalanceSchema>;

export interface BalanceBoxProps {
  title: ReactNode;
  label: string;
  placeholder?: string;
  onSave: (balance: string) => void;
  onDelete: () => void;
  defaultValue?: string;
}

export const BalanceBox = ({
  title,
  label,
  placeholder = "Enter USD amount",
  onSave,
  onDelete,
  defaultValue,
}: BalanceBoxProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    watch,
    register,
    reset,
    formState: { errors },
    handleSubmit,
  } = useForm<BalanceSchema>({
    resolver: zodResolver(zBalanceSchema),
    mode: "all",
    defaultValues: {
      balance:
        defaultValue !== undefined && !Number.isNaN(Number(defaultValue))
          ? Number(defaultValue)
          : undefined,
    },
  });

  const balance = watch("balance");

  const onEditClicked = () => setIsOpen(true);

  const onDeleteClicked = () => {
    reset();
    onDelete();
    setIsOpen(false);
  };

  const onSubmitValid: SubmitHandler<BalanceSchema> = ({ balance }) => {
    setIsOpen(false);
    onSave(`${balance}`);
  };

  const onSubmitInvalid: SubmitErrorHandler<BalanceSchema> = () => {
    toastError("Please enter a valid amount");
  };

  return (
    <form
      className={classNames(
        "flex min-h-[67px] flex-col justify-center rounded border border-gray-200 p-4 transition-all",
        isOpen && "gap-4 border-gray-500",
      )}
      onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}
    >
      <div className="flex min-h-8 items-center justify-between gap-4">
        <h2 className="m-0 p-0 font-medium text-base text-gray-800">{title}</h2>
        {!isOpen && balance ? (
          <div className="flex items-center justify-end gap-1">
            <Text className="font-mono text-base text-gray-800">
              {formatCash(balance)}
            </Text>
            <button onClick={onEditClicked} type="button">
              <PencilIcon size={20} />
            </button>
          </div>
        ) : (
          <Button
            variant="v2/ghost"
            onClick={() => setIsOpen(true)}
            type="button"
            size="v2/small"
          >
            <PlusIcon size={16} /> Specify Amount
          </Button>
        )}
      </div>
      <div
        className={classNames(
          "flex max-h-0 flex-col gap-1 overflow-hidden opacity-0 transition-all",
          isOpen &&
            "max-h-[600px] border-t border-t-cool-gray-300 pt-4 opacity-100",
        )}
      >
        <Input
          label={label}
          toolTip={
            <div className="ml-auto">
              <Tooltip
                content={
                  <>
                    Your account may have a margin loan or recent unsettled
                    purchases. A negative value means you've used borrowed funds
                    or owe money to your broker.
                    <br />
                    <br />
                    If you have a margin balance you would like to transfer,
                    enter a negative number.
                  </>
                }
              >
                <Text className="flex items-center gap-1 font-medium text-gray-700 text-sm">
                  <InfoIcon size={20} />
                </Text>
              </Tooltip>
            </div>
          }
          placeholder={placeholder}
          status={errors?.balance?.message ? "error" : "neutral"}
          errorMsg={errors?.balance?.message && "Please enter a valid amount"}
          type="number"
          step="any"
          msgPadded
          {...register("balance")}
        />
        <div className="flex items-center justify-between gap-4">
          <Button
            variant="v2/stroke"
            onClick={onDeleteClicked}
            disabled={!isOpen}
          >
            Delete
          </Button>
          <Button variant="v2/filled" type="submit" disabled={!isOpen}>
            Save
          </Button>
        </div>
      </div>
    </form>
  );
};
